// レイヤー詳細モーダル。
//
// 役割: レイヤー行の詳細ボタンから開き、レイヤーID・フィーチャー数・エクスポート・
//       属性テーブルのプレビュー（先頭 10 件）を表示する。来歴アコーディオンの後継。
// 関係: 属性プレビューは onLoadPreview（App が describeLayer を注入）で取得する。
//       コンポーネントは DuckDB を直接触らず、フック経由のハンドラに委ねる。
import { useEffect, useState } from 'react'
import { COLOR_SCHEMES } from '../viz/color-schemes'

function rgbToHex(rgb) {
  return `#${(rgb ?? [0, 0, 0]).map((value) => Number(value).toString(16).padStart(2, '0')).join('')}`
}

function hexToRgb(hex) {
  return [1, 3, 5].map((offset) => Number.parseInt(hex.slice(offset, offset + 2), 16))
}

function AttributeTable({ columns, sample }) {
  if (!columns || columns.length === 0) {
    return <p className="detail-empty">属性列なし（ジオメトリのみ）</p>
  }
  return (
    <div className="attr-table-wrap">
      <table className="attr-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.name} title={`${c.name}: ${c.type}`}>
                {c.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sample.map((row, i) => (
            // 静的なプレビュー表示で並び替えがないため index を key に使う
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <tr key={i}>
              {columns.map((c) => {
                const v = row[c.name]
                const text = v == null ? '' : String(v)
                return (
                  <td key={c.name} title={text}>
                    {text}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function LayerDetailModal({ layer, onClose, onExport, onLoadPreview, onAppearanceChange }) {
  // レイヤーごとに key で再マウントする前提なので、初期 state がそのまま読み込み中を表す
  // （effect 内で同期的に setState して再描画を増やさない）。
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let ignore = false
    onLoadPreview(layer.layerId)
      .then((res) => {
        if (ignore) return
        setPreview({ columns: res.columns ?? [], sample: res.sample ?? [] })
      })
      .catch((e) => {
        if (ignore) return
        setError(String(e?.message ?? e))
      })
      .finally(() => {
        if (!ignore) setLoading(false)
      })
    return () => {
      ignore = true
    }
  }, [layer.layerId, onLoadPreview])

  // 属性列から geom 由来を除いた列だけがプレビュー対象（describeLayer が geom を除外済み）。
  const attrColumns = preview?.columns ?? []
  const baseColor = layer.color ?? [255, 140, 0]
  const isChoropleth = layer.style?.type === 'choropleth'
  const schemes = isChoropleth
    ? COLOR_SCHEMES[layer.style.mode === 'categorical' ? 'categorical' : 'numeric']
    : []

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h3>{layer.displayName ?? layer.layerId}</h3>

        <dl className="detail-fields">
          <dt>レイヤーID</dt>
          <dd>
            <code>{layer.layerId}</code>
          </dd>
          <dt>フィーチャー数</dt>
          <dd>{layer.featureCount ?? '—'}</dd>
          <dt>エクスポート</dt>
          <dd>
            <button className="link-btn" onClick={() => onExport(layer.layerId, 'geojson')}>
              GeoJSON
            </button>
            {' / '}
            <button className="link-btn" onClick={() => onExport(layer.layerId, 'geoparquet')}>
              GeoParquet
            </button>
          </dd>
        </dl>

        <div className="detail-appearance">
          <div className="detail-preview-label">描画設定</div>
          <div className="appearance-controls">
            {!isChoropleth && (
              <label>
                <span>Fill</span>
                <input
                  type="color"
                  value={rgbToHex(layer.fillColor ?? baseColor)}
                  onChange={(event) =>
                    onAppearanceChange(layer.layerId, { fillColor: hexToRgb(event.target.value) })
                  }
                />
              </label>
            )}
            <label>
              <span>Stroke</span>
              <input
                type="color"
                value={rgbToHex(layer.strokeColor ?? baseColor)}
                onChange={(event) =>
                  onAppearanceChange(layer.layerId, { strokeColor: hexToRgb(event.target.value) })
                }
              />
            </label>
            {isChoropleth && (
              <label className="appearance-scheme">
                <span>Fillカラースケール</span>
                <select
                  value={layer.style.scheme}
                  onChange={(event) =>
                    onAppearanceChange(layer.layerId, { scheme: event.target.value })
                  }
                >
                  {schemes.map((scheme) => (
                    <option key={scheme} value={scheme}>
                      {scheme}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </div>

        <div className="detail-preview">
          <div className="detail-preview-label">属性プレビュー（先頭 10 件）</div>
          {loading && <p className="detail-empty">読み込み中…</p>}
          {error && <p className="detail-error">プレビューの取得に失敗しました: {error}</p>}
          {!loading && !error && <AttributeTable columns={attrColumns} sample={preview.sample} />}
        </div>

        <div className="modal-actions">
          <button onClick={onClose}>閉じる</button>
        </div>
      </div>
    </div>
  )
}

export default LayerDetailModal
